import { Components, switchClasses, Theme, touchRippleClasses } from '@mui/material';
import { GetThemeOptions } from '../../theme/theme';

/** Material Icons 24×24 paths (same shapes as mdi Check / Close). */
const SWITCH_THUMB_ICON_CHECK_PATH = 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z';
const SWITCH_THUMB_ICON_CLOSE_PATH =
  'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z';

function switchThumbIconDataUrl(pathD: string, fill: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${fill}" d="${pathD}"/></svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

/**
 * Component overrides (i.e. global styles, default props) applied to
 * MuiSwitch to make it match the design system.
 *
 * See: https://mui.com/material-ui/api/switch/#css
 */
export const getMuiSwitch = (opts: GetThemeOptions): Components<Theme>['MuiSwitch'] => ({
  defaultProps: {
    color: 'primary',
    // Ripple animates under the thumb during toggle and reads like a shadow/glow while the knob slides.
    ...(opts.panwRebrandStubYear && { disableRipple: true, disableFocusRipple: true }),
  },
  // MUI doesn't provide the needed level of granularity via content slots, so overriding
  // with CSS classes instead
  styleOverrides: {
    root: ({ theme }) => {
      const commonStyles = { padding: 9 };
      if (!theme.featureFlags.panwRebrandStubYear) {
        return commonStyles;
      }
      const { switch: switchPalette } = theme.palette;
      return {
        ...commonStyles,
        [`&:not(:has(.${switchClasses.disabled}))`]: {
          [`&:hover:not(:has(.${switchClasses.checked})) .${switchClasses.track}`]: {
            backgroundColor: switchPalette.trackHoverColor,
          },
          [`&:hover:has(.${switchClasses.checked}) .${switchClasses.track}`]: {
            backgroundColor: switchPalette.trackCheckedHoverColor,
          },
        },
      };
    },
    switchBase: ({ theme }) => {
      const { switch: switchPalette } = theme.palette;
      return {
        margin: 1,
        ...(theme.featureFlags.panwRebrandStubYear
          ? {
              // MUI adds a circular hover wash on the thumb (reads like a soft shadow)
              '&:hover': { backgroundColor: 'transparent' },
              [`&.${switchClasses.checked}:hover`]: { backgroundColor: 'transparent' },
              [`&:not(.${switchClasses.checked}):not(.${switchClasses.disabled}) > .${switchClasses.thumb}`]: {
                backgroundColor: switchPalette.thumbUncheckedColor,
                backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CLOSE_PATH, theme.palette.text.default),
              },
              [`&.${switchClasses.checked}:not(.${switchClasses.disabled}) > .${switchClasses.thumb}`]: {
                backgroundColor: switchPalette.thumbCheckedColor,
                backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CHECK_PATH, theme.palette.text.bold),
              },
              [`&.${switchClasses.disabled}:not(.${switchClasses.checked}) > .${switchClasses.thumb}`]: {
                backgroundColor: switchPalette.thumbDisabledColor,
                backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CLOSE_PATH, theme.palette.text.disabled),
              },
              [`&.${switchClasses.disabled}.${switchClasses.checked} > .${switchClasses.thumb}`]: {
                backgroundColor: switchPalette.thumbDisabledColor,
                backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CHECK_PATH, theme.palette.text.disabled),
              },
              [`& .${touchRippleClasses.root}`]: { display: 'none' },
              [`&.Mui-focusVisible .${switchClasses.thumb}`]: {
                // bringing in the default browser focus ring, rather than using the MUI focus ring
                outline: '2px auto -webkit-focus-ring-color',
                outlineOffset: 2,
              },
            }
          : {}),
        [`&.${switchClasses.checked}`]: {
          [`& + .${switchClasses.track}`]: {
            backgroundColor: switchPalette.trackCheckedColor,
            opacity: 1,
          },
        },
        [`&.${switchClasses.disabled}`]: {
          ...(!theme.featureFlags.panwRebrandStubYear
            ? {
                [`& > .${switchClasses.thumb}`]: {
                  backgroundColor: switchPalette.thumbDisabledColor,
                },
              }
            : {}),
          [`& + .${switchClasses.track}`]: {
            opacity: theme.featureFlags.panwRebrandStubYear ? 1 : 0.3,
            ...(theme.featureFlags.panwRebrandStubYear && {
              backgroundColor: switchPalette.trackDisabledColor,
            }),
          },
        },
      };
    },
    thumb: ({ theme }) => {
      const size = theme.featureFlags.panwRebrandStubYear ? { height: 16, width: 16 } : { height: 18, width: 18 };
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          ...size,
          boxShadow: 'none',
          margin: '1px 0 0 1px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '12px 12px',
        };
      }
      const { switch: switchPalette } = theme.palette;
      return {
        ...size,
        backgroundColor: switchPalette.thumbColor ?? switchPalette.thumbUncheckedColor,
      };
    },
    track: ({ theme }) => {
      return {
        backgroundColor: theme.palette.switch.trackUncheckedColor,
        ...(theme.featureFlags.panwRebrandStubYear && {
          border: `1px solid ${theme.palette.border.default}`,
        }),
        borderRadius: 12,
        opacity: 1,
      };
    },
  },
});
